import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { PreviewLink } from '../../types/previewLink';
import {
  bulkGeneratePreviewLinks,
  previewPublicUrl,
  type BulkPreviewLinksResult,
} from './api';

// Ad Preview Mockups Stage 1 §3.2. Bulk modal mounted from the Master
// Grid's BulkActionBar. The button that opens it is gated to single-
// Campaign selections via the same resolveBulkRefreshTarget helper the
// Refresh from Campaign bulk action uses (the gate semantics are
// identical — every selected row must share one Campaign).
//
// The modal has two phases:
//   1. Pre-generate — shows the list of selected rows + a confirm button
//      ("Generate N preview links"). Lets the AM bail before mutating.
//   2. Post-generate — table of generated URLs with copy-as-table and
//      CSV download per spec §3.2.
//
// On partial failure (some rows succeed, others rejected) both lists
// surface together: the success table at the top, rejected list below
// with the reason from the server.

type RowDescriptor = {
  rowId: string;
  locationName: string;
  brandName: string | null;
};

type Props = {
  rolloutId: string;
  // Caller projects the selected rows down to a (rowId, location, brand)
  // tuple so this modal stays presentation-only and doesn't need to know
  // about Account / location-profile shapes.
  rows: RowDescriptor[];
  onClose: () => void;
};

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

export function BulkPreviewModal({ rolloutId, rows, onClose }: Props) {
  const qc = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [result, setResult] = useState<BulkPreviewLinksResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rowIds = useMemo(() => rows.map((r) => r.rowId), [rows]);

  const generateMut = useMutation({
    mutationFn: () => bulkGeneratePreviewLinks(rolloutId, rowIds),
    onSuccess: (res) => {
      setResult(res);
      setError(null);
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
    },
    onError: (err: Error) => {
      setError(
        err instanceof ApiError ? err.message : 'Bulk preview generation failed.',
      );
    },
  });

  // Pair each generated link to its location label by joining on rowId.
  const tableRows = useMemo(() => {
    if (!result) return [];
    const byId = new Map(rows.map((r) => [r.rowId, r] as const));
    return result.links.map((link: PreviewLink) => {
      const meta = byId.get(link.rowId);
      return {
        locationName: meta?.locationName ?? '(unknown location)',
        brandName: meta?.brandName ?? '',
        url: previewPublicUrl(link.token),
        expiresAt: link.expiresAt,
      };
    });
  }, [result, rows]);

  const copyAsTable = async () => {
    if (tableRows.length === 0) return;
    const text = tableRows
      .map((r) => `${r.locationName}\t${r.url}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setError('Could not copy — select the table cells manually.');
    }
  };

  const downloadCsv = () => {
    if (tableRows.length === 0) return;
    const header = 'Location,Brand,Preview URL,Expires\n';
    const body = tableRows
      .map((r) =>
        [r.locationName, r.brandName, r.url, r.expiresAt]
          .map(csvEscape)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `preview-links-${rolloutId}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-3xl rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-preview-title"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <h2
              id="bulk-preview-title"
              className="text-lg font-semibold text-slate-900"
            >
              Generate previews — {rows.length} location
              {rows.length === 1 ? '' : 's'}
            </h2>
            <p className="text-sm text-slate-500">
              One public URL per row, default expiry 14 days. Re-generation
              mints a NEW URL per row but doesn't invalidate any previous
              links — those keep working until their own expiry.
            </p>
          </div>

          {!result && (
            <div className="rounded-md border border-slate-200 max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.rowId} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{r.locationName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result && tableRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-700">
                  {tableRows.length} preview link
                  {tableRows.length === 1 ? '' : 's'} generated.
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={copyAsTable}>
                    {copyState === 'copied' ? 'Copied!' : 'Copy as table'}
                  </Button>
                  <Button type="button" variant="outline" onClick={downloadCsv}>
                    Download CSV
                  </Button>
                </div>
              </div>
              <div className="rounded-md border border-slate-200 max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Location</th>
                      <th className="text-left px-3 py-2">Preview URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r) => (
                      <tr key={r.url} className="border-t border-slate-100">
                        <td className="px-3 py-1.5">{r.locationName}</td>
                        <td className="px-3 py-1.5 font-mono text-xs break-all">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:underline"
                          >
                            {r.url}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && result.rejected.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 space-y-1">
              <p className="font-medium">
                {result.rejected.length} row
                {result.rejected.length === 1 ? '' : 's'} skipped:
              </p>
              <ul className="list-disc pl-5">
                {result.rejected.map((r) => (
                  <li key={r.rowId}>{r.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              disabled={generateMut.isPending}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {result ? 'Done' : 'Cancel'}
            </button>
            {!result && (
              <Button
                type="button"
                onClick={() => generateMut.mutate()}
                disabled={generateMut.isPending || rows.length === 0}
              >
                {generateMut.isPending
                  ? 'Generating…'
                  : `Generate ${rows.length} link${rows.length === 1 ? '' : 's'}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Re-export AccountCampaign so call sites can type their projection
// fluently without an extra import.
export type { AccountCampaign };
